import * as signalR from '@microsoft/signalr';
import { getToken } from '@lcp/http-auth';
import dayjs from 'dayjs';
import { userSettingsApi } from '@/api/powerMarkets/userSettingsApi.ts';
import { BrowserRefreshWarningText, useAlertStore } from '@/stores/alertStore.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useBingoStore } from '@/stores/powerMarkets/bingoStore.ts';
import { useDataHubStore } from '@/stores/powerMarkets/dataHubStore.ts';
import { IBrowserNotificationPayload } from '@/interfaces/powerMarkets/notifications/IBrowserNotificationPayload.ts';
import { useLiveTableStore } from '@/stores/powerMarkets/liveTableStore.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { IPeriodData } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import { ITablePushData } from '@/interfaces/powerMarkets/table/ILiveTablePushData.ts';
import { IEarliestDateInBuffer } from '@/interfaces/powerMarkets/static/IEarliestDateInBuffer.ts';
import { IBingoPlantData } from '@/interfaces/powerMarkets/bingo/IBingoPlantData.ts';
import { apiEndpointUrls } from '@/api/apiEndpointConfig.ts';

enum availableGroups {
    status = 'status',
    page = 'page',
    module = 'module',
    series = 'series',
    browserNotifications = 'browserNotifications',
    globalNotifications = 'globalNotifications',
}

export type availableGroupsType = keyof typeof availableGroups;
export type SignalRMode = 'authenticated' | 'free' | null;

export class SignalRConnection {
    static connection: signalR.HubConnection | null = null;

    static signalRUrlBase = apiEndpointUrls.signalR;

    static groupsJoined: Record<string, number> = {};

    static currentBearerToken: string | null = null;

    static useAuthenticatedConnection = false;

    static shouldReconnect = false;

    static setBearerToken (bearerToken: string | null): void {
        this.currentBearerToken = bearerToken;
    }

    static async sync (signalRMode: SignalRMode, bearerToken: string | null): Promise<void> {
        if (signalRMode === null) {
            if (!this.connection) return;
            this.setBearerToken(null);
            await this.disconnect();
            return;
        }

        const useAuthenticatedConnection = signalRMode === 'authenticated';
        const bearerTokenChanged = this.currentBearerToken !== bearerToken;
        const connectionNeedsRefresh = !this.connection
            || this.useAuthenticatedConnection !== useAuthenticatedConnection;

        if (bearerTokenChanged) {
            this.setBearerToken(bearerToken);
        }

        if (connectionNeedsRefresh) {
            await this.initialise(useAuthenticatedConnection);
        }
    }

    // #region initialising signalR connection
    static async initialise (useAuthenticatedConnection = false): Promise<void> {
        if (this.connection) {
            await this.disconnect();
        }
        this.shouldReconnect = true;
        this.useAuthenticatedConnection = useAuthenticatedConnection;
        const signalRUrlTarget = this.useAuthenticatedConnection ? 'dataHub' : 'enactLite';
        const signalRUrl = `${this.signalRUrlBase}/${signalRUrlTarget}`;
        const signalROptions = this.useAuthenticatedConnection ? { accessTokenFactory: (): string => this.currentBearerToken ?? '' } : {};
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(signalRUrl, signalROptions)
            .build();
        this.initialisePersistentConnections();
        await this.start();
        this.connection.onclose(async (error) => {
            if (!this.shouldReconnect) return;
            console.log('disconnect onclosed', error);
            await this.refreshBearerToken();
            await this.start(true);
        });
    }

    static async start (restart?: boolean): Promise<boolean> {
        if (!this.connection) return false;
        return this.connection.start().then(() => {
            this.joinGroup('status', '');
            this.joinGroup('globalNotifications', '');
            if (restart) {
                this.resubscribeToGroups();
            }
            console.log('Successful connection to Enact Push Service 😃📤');
            return true;
        }).catch(() => {
            console.count('Failed to connect to Enact Push Service 😢📤');
            setTimeout(async () => {
                if (!this.shouldReconnect) return;
                await this.start();
            }, 5000);
            return false;
        });
    }

    static async refreshBearerToken (): Promise<void> {
        if (!this.useAuthenticatedConnection) return;
        const scope = import.meta.env.VITE_APP_AUTH_SCOPE ? [import.meta.env.VITE_APP_AUTH_SCOPE] : null;
        let token: string | false | null;
        try {
            token = await getToken(scope);
        } catch {
            token = null;
        }
        this.setBearerToken(token === false ? null : token);
    }

    // #region Helper methods

    static initialisePersistentConnections (): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const persistentConnections: Record<string, (data?: any) => unknown> = {
            heartbeat: this.heartbeatCallBack,
            globalNotifications: this.globalNotificationsCallback,
            periodChange: this.periodChangeCallback,
            replenishSubscriptions: this.replenishSubscriptionsCallback,
        };

        if (this.useAuthenticatedConnection) {
            persistentConnections.newReleaseRefreshNeeded = this.newReleaseRefreshNeededCallback;
            persistentConnections.browserUpdate = this.addBrowserUpdateCallback;
            persistentConnections.notificationRefresh = this.notificationRefreshCallback;
            persistentConnections.earliestDateInBuffer = this.earliestDateInBufferCallback;
            persistentConnections.nonBmStorUpdate = this.nonBmStorUpdateCallback;
            persistentConnections.liveTableUpdate = this.liveTableUpdateCallback;
            persistentConnections.bingo = this.bingoCallback;
        }

        Object.entries(persistentConnections).forEach(([listenerId, callBack]) => {
            this.listenTo(listenerId, callBack);
        });
    }

    static async disconnect (): Promise<void> {
        this.shouldReconnect = false;
        if (this.connection) {
            await this.connection.stop();
        }
        this.groupsJoined = {};
        this.connection = null;
        this.useAuthenticatedConnection = false;
    }
    // #endregion

    static addUserBrowserSubscriptions (): void {
        const userSettingsStore = useUserSettingsStore();
        this.joinGroup('browserNotifications', userSettingsStore.userSettings.otherSettings.data.userId);
        userSettingsApi.setUserOnline();
    }

    // #region connect to groups
    static getModuleGroup (groupType: string, groupId: string): string {
        const userSettingsStore = useUserSettingsStore();
        switch (groupType) {
            case availableGroups.status:
                return 'status';
            case availableGroups.page:
                return `page_${groupId}_${userSettingsStore.userCountry}`;
            case availableGroups.module:
                return `module_${groupId}`;
            case availableGroups.series:
                return `chartUpdate&${groupId}`;
            case availableGroups.browserNotifications:
                return `browsernotifications${groupId}`;
            case availableGroups.globalNotifications:
                return 'globalNotifications';
        }
        return `${groupType}${groupId}`;
    }

    static joinGroup (groupType: availableGroupsType, groupId: string): void {
        if (!this.connection) return;
        const moduleGroup = this.getModuleGroup(groupType, groupId);
        if (!this.groupsJoined[moduleGroup]) {
            this.connection.invoke('JoinGroup', moduleGroup);
            this.groupsJoined[moduleGroup] = 0;
        }
        this.groupsJoined[moduleGroup] += 1;
    }

    static leaveGroup (groupType: availableGroupsType, groupId: string): void {
        if (!this.connection) return;
        const moduleGroup = this.getModuleGroup(groupType, groupId);
        if (!this.groupsJoined[moduleGroup] || this.groupsJoined[moduleGroup] <= 0) return;
        this.groupsJoined[moduleGroup] -= 1;
        if (this.groupsJoined[moduleGroup] === 0) {
            delete this.groupsJoined[moduleGroup];
            this.connection.invoke('LeaveGroup', moduleGroup);
        }
    }

    static resubscribeToGroups (): void {
        if (!this.connection) return;
        Object.keys(this.groupsJoined).forEach((group) => this.connection!.invoke('JoinGroup', group));
    }
    // #endregion

    // region listenerId connection

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static listenTo (listenerId: string, callBack: (data?: any) => unknown): void {
        if (!this.connection) return;
        this.connection.on(listenerId, callBack);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static endListen (listenerId: string, callBack: (data?: any) => unknown): void {
        if (!this.connection) return;
        this.connection.off(listenerId, callBack);
    }
    // endregion

    // #region subscription callbacks
    static heartbeatCallBack (data: string): void {
        const dataHubStore = useDataHubStore();

        const oldTime = dayjs(dataHubStore.lastHeartbeat, 'DD/MM/YYYY HH:mm:ss');
        const newTime = dayjs(data, 'DD/MM/YYYY HH:mm:ss');

        if (Math.abs(oldTime.diff(newTime, 'milliseconds')) > 5 * 60 * 1000) {
            const alertStore = useAlertStore();
            alertStore.pushGlobalPowerMarketsNotification(BrowserRefreshWarningText);
        }

        dataHubStore.lastHeartbeat = data;
    }

    static globalNotificationsCallback (data: string): void {
        if (data.includes('Missing BSADs') || data.includes('SO Flagged £0/MWh')) return; // Included until we stop sending from the backend
        const alertStore = useAlertStore();
        alertStore.pushGlobalPowerMarketsNotification(data);
    }

    static async periodChangeCallback (data: IPeriodData): Promise<void> {
        await SignalRConnection.refreshBearerToken();
        SignalRConnection.resubscribeToGroups();
        const dataStore = useDataStore();
        dataStore.updatePeriodData(data);
    }

    static replenishSubscriptionsCallback (): void {
        if (!SignalRConnection.useAuthenticatedConnection) return;
        userSettingsApi.setUserOnline();
    }

    static newReleaseRefreshNeededCallback (data: string): void {
        const alertStore = useAlertStore();
        alertStore.setAccessDeniedModal('release', data);
        alertStore.setShowAccessDeniedModal(true);
    }

    static addBrowserUpdateCallback (data: IBrowserNotificationPayload): void {
        const dataHubStore = useDataHubStore();
        dataHubStore.pushBrowserNotification(data);
    }

    static notificationRefreshCallback (data: number): void {
        const liveTableStore = useLiveTableStore();
        liveTableStore.deleteLastHourNotifications(data);
    }

    static nonBmStorUpdateCallback (data: string): void {
        const dataHubStore = useDataHubStore();
        dataHubStore.nonBmStorUpdate = data;
    }

    static earliestDateInBufferCallback (data: IEarliestDateInBuffer): void {
        const dataStore = useDataStore();
        const staticData = dataStore.staticStore;
        const bufferDate = staticData.endpointToEarliestBufferDate[data.endpoint.toLowerCase()];
        if (bufferDate) {
            bufferDate.earliestDayInBuffer = data.day === null ? null : new Date(data.year, data.month - 1, data.day);
        }
    }

    static bingoCallback (data: IBingoPlantData): void {
        if (!data?.plantId) return;
        const bingoStore = useBingoStore();
        bingoStore.updateBingoPlantFromPush(data);
    }

    static liveTableUpdateCallback (data: ITablePushData): void {
        const liveTableStore = useLiveTableStore();
        liveTableStore.handleTablePush(data);
    }
    // #endregion
}
